import React, { useState } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { Product, StoreProductOverride } from '../services/api';
import { ProductPricingData } from '../components/ProductPricingBadge';
import { getEffectiveMapForProduct, resolveDisplaySale, resolveRebateDisplay } from './mapPricing';
import { classifyVariantKind, isSelfVariantRow } from './productVariants';
import { stripHtml } from './text';
import { useLocale } from '../contexts/LocaleContext';

/**
 * Shared helpers for the product-detail dialog (`components/productDetails/`)
 * and `CameraBrowserPage.tsx`'s card/list rendering. Extracted so both sides
 * of the split use one implementation instead of forking behavior.
 */

// Extended camera with pricing data (mirrors CameraBrowserPage's local extension).
export interface CameraWithPricing extends Product {
  pricingData?: ProductPricingData;
  /** Works-with suggestions; the server sends it only with flag `lens_compatibility` on. */
  compatibility?: import('../components/productDetails/ProductWorksWith').ProductCompatibility;
}

/** Normalizes a `getPricingByModel`-shaped result into `ProductPricingData` (or undefined when there's nothing worth attaching). */
export function toListingPricingData(pricingResult: {
  mapPrice?: number;
  catalogMapIsSynthetic?: boolean;
  msrp?: number;
  instantRebate?: number;
  tradeCredit?: number;
  dealerPrice?: number;
  sellingPriceAfterIR?: number;
  effectiveDate?: string | Date;
  endDate?: string | Date;
  status?: ProductPricingData['status'];
  priceType?: string;
  sellingPrice?: number;
  region?: ProductPricingData['region'];
  comboOffers?: ProductPricingData['comboOffers'];
  pastRebates?: Array<{
    instantRebate: number;
    mapPrice?: number | null;
    sellingPriceAfterIR?: number | null;
    effectiveDate?: string | Date | null;
    endDate?: string | Date | null;
  }>;
  vendorPrivateLocked?: boolean;
} | null | undefined): ProductPricingData | undefined {
  if (!pricingResult) return undefined;
  const comboOffers = (pricingResult.comboOffers || []).map((offer) => ({
    ...offer,
    effectiveDate: offer.effectiveDate ? new Date(offer.effectiveDate) : undefined,
    endDate: offer.endDate ? new Date(offer.endDate) : undefined,
  }));
  const pastRebates = (pricingResult.pastRebates || [])
    .filter((r) => Number(r.instantRebate) > 0)
    .map((r) => ({
      instantRebate: Number(r.instantRebate),
      mapPrice: r.mapPrice ?? null,
      sellingPriceAfterIR: r.sellingPriceAfterIR ?? null,
      effectiveDate: r.effectiveDate ? new Date(r.effectiveDate) : null,
      endDate: r.endDate ? new Date(r.endDate) : null,
    }));
  if (
    !pricingResult.mapPrice &&
    !pricingResult.instantRebate &&
    !pricingResult.tradeCredit &&
    !pricingResult.dealerPrice &&
    comboOffers.length === 0 &&
    pastRebates.length === 0 &&
    pricingResult.catalogMapIsSynthetic !== true &&
    pricingResult.vendorPrivateLocked !== true &&
    !(Number(pricingResult.sellingPrice) > 0)
  ) {
    return undefined;
  }
  return {
    mapPrice: pricingResult.mapPrice,
    catalogMapIsSynthetic: pricingResult.catalogMapIsSynthetic,
    msrp: pricingResult.msrp,
    sellingPrice: pricingResult.sellingPrice,
    instantRebate: pricingResult.instantRebate,
    tradeCredit: pricingResult.tradeCredit,
    dealerPrice: pricingResult.dealerPrice,
    sellingPriceAfterIR: pricingResult.sellingPriceAfterIR,
    effectiveDate: pricingResult.effectiveDate ? new Date(pricingResult.effectiveDate) : undefined,
    endDate: pricingResult.endDate ? new Date(pricingResult.endDate) : undefined,
    status: pricingResult.status,
    priceType: pricingResult.priceType,
    region: pricingResult.region,
    comboOffers,
    pastRebates,
    vendorPrivateLocked: pricingResult.vendorPrivateLocked === true ? true : undefined,
  };
}

type DealerCostInput = {
  pricing?: {
    wholesale?: number;
    promasterDealerCost?: { amount: number; asOf: string; stale: boolean } | null;
  } | null;
  pricingData?: { dealerPrice?: number } | null;
};

export interface DealerCostResolution {
  amount: number;
  source: 'sheet' | 'promaster' | 'catalog';
  /** Promaster only: when Promaster last reported the cost. */
  asOf?: string;
  stale?: boolean;
}

const positive = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Authorized-dealer net for this SKU, in the server's order (`dealerCostLookup`):
 * sheet `dealerPrice` (the store's own, else the manufacturer's), then a fresh
 * Promaster cost, then catalog `pricing.wholesale`, then a stale Promaster cost.
 * The API already stripped all of them unless the store is verified for this
 * product's brand.
 */
export function resolveDealerCostWithSource(product: DealerCostInput): DealerCostResolution | null {
  const fromSheet = positive(product.pricingData?.dealerPrice);
  if (fromSheet) return { amount: fromSheet, source: 'sheet' };
  const promaster = product.pricing?.promasterDealerCost;
  const promasterAmount = positive(promaster?.amount);
  const fromPromaster: DealerCostResolution | null = promaster && promasterAmount
    ? { amount: promasterAmount, source: 'promaster', asOf: promaster.asOf, stale: promaster.stale === true }
    : null;
  if (fromPromaster && !fromPromaster.stale) return fromPromaster;
  const fromCatalog = positive(product.pricing?.wholesale);
  if (fromCatalog) return { amount: fromCatalog, source: 'catalog' };
  return fromPromaster;
}

export function resolveDealerCost(product: DealerCostInput): number {
  return resolveDealerCostWithSource(product)?.amount ?? 0;
}

export interface BrowserActionFeedback {
  severity: 'success' | 'warning' | 'error' | 'info';
  message: string;
}

// ---------------------------------------------------------------------------
// Images / misc formatting
// ---------------------------------------------------------------------------

export const getImageUrl = (img: string | { url?: string } | undefined): string | undefined => {
  if (!img) return undefined;
  const url = typeof img === 'string' ? img : img?.url;
  return typeof url === 'string' ? url : undefined;
};

/** Normalizes protocol-relative image URLs (e.g. //example.com -> https://example.com). */
export const toAbsoluteImageUrl = (img: string | { url?: string } | undefined): string | undefined => {
  const url = getImageUrl(img);
  if (!url) return undefined;
  return url.startsWith('//') ? `https:${url}` : url;
};

export const formatStockStatus = (status?: string | null): string =>
  String(status || '').replace(/_/g, ' ').trim();

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

function asDate(date: any): Date | null {
  if (!date) return null;
  if (typeof date.toDate === 'function') {
    const converted = date.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
  }
  const secs = date?.seconds ?? date?._seconds;
  if (typeof secs === 'number') {
    const converted = new Date(secs * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }
  if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;
  if (typeof date === 'string' || typeof date === 'number') {
    const converted = new Date(date);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }
  return null;
}

/** Manufacturer sheet days are stored at midnight UTC, not as a local instant. */
function isUtcMidnight(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

export const formatDate = (date: any): string | null => {
  if (!date) return null;
  const parsed = asDate(date);
  if (!parsed) return typeof date === 'string' ? date : null;
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    // Sep 28 00:00Z is the file's Sep 28. Local time would show Sep 27 in the US.
    ...(isUtcMidnight(parsed) ? { timeZone: 'UTC' } : {}),
  });
};

/** "1 spec" / "2 specs": the count with its noun in the right number. */
export const pluralize = (count: number, singular: string, plural: string = `${singular}s`): string =>
  `${count.toLocaleString()} ${count === 1 ? singular : plural}`;

export const formatRelativeDate = (date: any): string | null => {
  if (!date) return null;

  let dateObj: Date;

  if (date?.toDate) {
    dateObj = date.toDate();
  } else if (typeof (date?.seconds ?? date?._seconds) === 'number') {
    dateObj = new Date((date.seconds ?? date._seconds) * 1000);
  } else {
    dateObj = new Date(date);
  }

  if (isNaN(dateObj.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${pluralize(diffDays, 'day')} ago`;
  if (diffDays < 30) return `${pluralize(Math.floor(diffDays / 7), 'week')} ago`;
  if (diffDays < 365) return `${pluralize(Math.floor(diffDays / 30), 'month')} ago`;
  return `${pluralize(Math.floor(diffDays / 365), 'year')} ago`;
};

/** Tooltip copy for freshness dates: explanation plus the absolute date. */
export const freshnessTooltip = (explanation: string, date?: any): string => {
  const abs = formatDate(date);
  return abs ? `${explanation} (${abs})` : explanation;
};

export const lastPriceChangeAt = (product: { pricing?: Product['pricing'] }): unknown =>
  product.pricing?.vendorPriceAsOf ?? product.pricing?.priceAsOf;

function dateToMs(date: any): number {
  if (!date) return 0;
  let dateObj: Date;
  if (date?.toDate) dateObj = date.toDate();
  else if (typeof (date?.seconds ?? date?._seconds) === 'number') dateObj = new Date((date.seconds ?? date._seconds) * 1000);
  else dateObj = new Date(date);
  const ms = dateObj.getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Newest of several date-ish values (Firestore Timestamp, Date, ISO string). */
export const laterDate = (...dates: any[]): any => {
  let best: any = null;
  let bestMs = 0;
  for (const date of dates) {
    if (!date) continue;
    const ms = dateToMs(date);
    if (!ms) continue;
    if (ms >= bestMs) {
      best = date;
      bestMs = ms;
    }
  }
  return best;
};

/** Age in whole days for manufacturer price/stock freshness warnings (null if unknown). */
export const daysSinceDate = (date: any): number | null => {
  if (!date) return null;
  const ms = dateToMs(date);
  if (!ms) return null;
  return Math.floor((Date.now() - ms) / (1000 * 60 * 60 * 24));
};

/** Whole days remaining until a future date (e.g. a rebate's end date); null if unknown. */
export const daysUntilDate = (date: any): number | null => {
  if (!date) return null;
  const ms = dateToMs(date);
  if (!ms) return null;
  return Math.ceil((ms - Date.now()) / (1000 * 60 * 60 * 24));
};

// ---------------------------------------------------------------------------
// Price-change history
// ---------------------------------------------------------------------------

export interface ProductPriceHistoryRow {
  id: string;
  changeType?: string;
  previousValue?: {
    mapPrice?: number;
    msrp?: number;
    retail?: number;
    salePrice?: number;
    instantRebate?: number;
  };
  newValue?: {
    mapPrice?: number;
    msrp?: number;
    retail?: number;
    salePrice?: number;
    instantRebate?: number;
    effectiveDate?: string | Date;
  };
  changedAt?: string | Date;
}

const historyMoney = (n?: number | null): string | null => {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return `$${Number(n).toFixed(2)}`;
};

/**
 * `formatMoney` defaults to a plain "$" formatter; pass the product-aware one
 * (see `formatMoneyForProduct`) so a CAD product's history isn't labelled in
 * a currency the rest of its price block doesn't use.
 */
export const historyPrimaryLine = (
  row: ProductPriceHistoryRow,
  formatMoney: (n?: number | null) => string | null = historyMoney,
): string => {
  if (row.changeType === 'sync_rejected') {
    const advertised = formatMoney(row.newValue?.salePrice);
    const floor = formatMoney(row.newValue?.mapPrice ?? row.newValue?.msrp);
    const kind = row.newValue?.mapPrice != null ? 'MAP' : 'MSRP';
    if (advertised && floor) {
      return `Rejected advertised ${advertised} (under 50% of ${kind} ${floor})`;
    }
    return advertised ? `Rejected advertised ${advertised}` : 'Rejected advertised price';
  }
  const next = row.newValue;
  const prev = row.previousValue;
  const parts: string[] = [];
  const map = formatMoney(next?.mapPrice);
  const was = formatMoney(prev?.mapPrice);
  if (map) parts.push(was && was !== map ? `MAP ${map} (was ${was})` : `MAP ${map}`);
  const retail = formatMoney(next?.retail);
  const wasRetail = formatMoney(prev?.retail);
  if (retail && retail !== map) {
    parts.push(wasRetail && wasRetail !== retail ? `Retail ${retail} (was ${wasRetail})` : `Retail ${retail}`);
  }
  const msrp = formatMoney(next?.msrp);
  if (msrp && msrp !== map && msrp !== retail) parts.push(`MSRP ${msrp}`);
  const sale = formatMoney(next?.salePrice);
  if (sale && sale !== map) parts.push(`Sale ${sale}`);
  const rebateAmt = Number(next?.instantRebate);
  const rebate = formatMoney(next?.instantRebate);
  const prevRebate = formatMoney(prev?.instantRebate);
  if (rebate && rebateAmt > 0) {
    parts.push(
      prevRebate && prevRebate !== rebate
        ? `Rebate ${rebate} (was ${prevRebate})`
        : `Rebate ${rebate}`
    );
  }
  return parts.join(' · ') || 'Price recorded';
};

const rejectedReviewHistoryRow = (
  review: NonNullable<Product['pricing']>['suspiciousDiscountReview'] | undefined
): ProductPriceHistoryRow | null => {
  if (!review || review.status !== 'rejected') return null;
  const advertised = Number(review.advertisedPrice);
  const floor = Number(review.floor);
  if (!Number.isFinite(advertised) || !Number.isFinite(floor)) return null;
  return {
    id: `suspicious-discount-rejected-${advertised}-${floor}`,
    changeType: 'sync_rejected',
    newValue: {
      salePrice: advertised,
      ...(review.floorKind === 'msrp' ? { msrp: floor } : { mapPrice: floor }),
    },
    changedAt: review.reviewedAt || review.firstSeenAt,
  };
};

const historyRowTimeMs = (row: ProductPriceHistoryRow): number => {
  const date: any = row.changedAt || row.newValue?.effectiveDate;
  return date ? dateToMs(date) : 0;
};

export const mergePriceHistoryWithRejectedReview = (
  rows: ProductPriceHistoryRow[],
  review: NonNullable<Product['pricing']>['suspiciousDiscountReview'] | undefined
): ProductPriceHistoryRow[] => {
  const synthetic = rejectedReviewHistoryRow(review);
  if (!synthetic) return rows;
  const advertisedCents = Math.round(Number(synthetic.newValue?.salePrice) * 100);
  const alreadyLogged = rows.some((row) => {
    if (row.changeType !== 'sync_rejected') return false;
    return Math.round(Number(row.newValue?.salePrice) * 100) === advertisedCents;
  });
  if (alreadyLogged) return rows;
  return [...rows, synthetic].sort(
    (a, b) => historyRowTimeMs(b) - historyRowTimeMs(a)
  );
};

// ---------------------------------------------------------------------------
// Market pricing refresh state
// ---------------------------------------------------------------------------

export const MARKET_PRICING_STALE_MS = 7 * 24 * 60 * 60 * 1000;
const MARKET_PRICING_LOCK_TTL_MS = 10 * 60 * 1000;

/** True when street/dealer rows exist but `checkedAt` is missing or older than 7 days. */
export function isMarketPricingStale(
  marketPricing: { checkedAt?: unknown } | null | undefined,
  nowMs = Date.now()
): boolean {
  if (!marketPricing) return false;
  const checkedMs = dateToMs(marketPricing.checkedAt);
  if (!checkedMs) return true;
  return nowMs - checkedMs >= MARKET_PRICING_STALE_MS;
}

export type MarketPricingRefreshUi =
  | { state: 'ready' }
  | { state: 'refreshing' }
  | { state: 'cooldown'; nextRefreshInDays: number };

export function getMarketPricingRefreshUi(
  marketPricing: Product['marketPricing'] | undefined,
  nowMs = Date.now()
): MarketPricingRefreshUi {
  const lockMs = dateToMs(marketPricing?.refreshingAt);
  if (lockMs && nowMs - lockMs < MARKET_PRICING_LOCK_TTL_MS) {
    return { state: 'refreshing' };
  }
  const checkedMs = dateToMs(marketPricing?.checkedAt);
  if (!checkedMs || nowMs - checkedMs >= MARKET_PRICING_STALE_MS) {
    return { state: 'ready' };
  }
  const remaining = MARKET_PRICING_STALE_MS - (nowMs - checkedMs);
  return {
    state: 'cooldown',
    nextRefreshInDays: Math.max(1, Math.ceil(remaining / (24 * 60 * 60 * 1000))),
  };
}

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

export function linkedVariantKindLabel(product: Pick<Product, 'parentSku' | 'name' | 'model'>): string {
  if (!product.parentSku) return 'Body';
  const kind = classifyVariantKind(product.name || product.model || '');
  if (kind === 'kit') return 'Kit';
  if (kind === 'bundle') return 'Bundle';
  if (kind === 'style') return 'Style';
  return 'Configuration';
}

const SKU_COLOR_SUFFIX: Record<string, string> = {
  B: 'black',
  S: 'silver',
  W: 'white',
  G: 'graphite',
};

export function variantOptionIsCurrent(
  product: Pick<Product, 'sku' | 'upc' | 'ean' | 'name' | 'model'> & { mpn?: string },
  variant: { sku?: string; mpn?: string; upc?: string; ean?: string; optionValue?: string },
): boolean {
  const sku = (variant.sku || variant.mpn || '').trim();
  if (isSelfVariantRow(product, variant)) return true;
  const color = (variant.optionValue || '').trim().toLowerCase();
  if (!sku && color) {
    const hay = `${product.name || ''} ${product.model || ''}`.toLowerCase();
    if (hay.includes(color)) return true;
    const compact = String(product.sku || '').replace(/[^A-Za-z0-9]/g, '');
    const suffix = compact.slice(-1).toUpperCase();
    if (SKU_COLOR_SUFFIX[suffix] === color) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Small presentational pieces used in the price block
// ---------------------------------------------------------------------------

export function RebateWindowChip({ status }: { status?: 'active' | 'upcoming' }) {
  if (status !== 'upcoming') return null;
  return (
    <Chip
      size="small"
      icon={<Schedule sx={{ fontSize: '14px !important' }} />}
      label="Upcoming"
      color="info"
      variant="outlined"
      sx={{
        height: 20,
        fontWeight: 700,
        fontSize: '0.65rem',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

export function DealerReimbursementNote({
  amount,
  formatAmount,
  compact,
}: {
  amount: number;
  formatAmount: (amount: number) => string;
  compact?: boolean;
}) {
  if (!(amount > 0)) return null;
  const amountLabel = formatAmount(amount);
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mb: compact ? 1 : 2 }}
    >
      {compact
        ? `Dealer reimbursement ${amountLabel}`
        : `Dealer reimbursement ${amountLabel} — manufacturer credit to the dealer, not advertised to customers`}
    </Typography>
  );
}

const DESCRIPTION_PREVIEW_LENGTH = 350;

export const ExpandableDescription: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const safeText = String(text || '');
  const preview = safeText.slice(0, DESCRIPTION_PREVIEW_LENGTH).replace(/\s+\S*$/, '') + '…';

  return (
    <Box>
      <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
        {expanded ? safeText : preview}
      </Typography>
      <Button
        size="small"
        onClick={() => setExpanded((prev) => !prev)}
        sx={{ textTransform: 'none', mt: 0.5, fontWeight: 500, px: 0 }}
      >
        {expanded ? 'Show less' : 'Read more'}
      </Button>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Store overrides form
// ---------------------------------------------------------------------------

export interface OverrideFormState {
  overrideTitle: string;
  overrideDescription: string;
  overrideTags: string;
  overridePrice: string;
  overridePriceStartAt: string;
  overridePriceEndAt: string;
  ignoreSalePrice: boolean;
  ignoreRebate: boolean;
  keepRebate: boolean;
}

export const EMPTY_OVERRIDE_FORM: OverrideFormState = {
  overrideTitle: '',
  overrideDescription: '',
  overrideTags: '',
  overridePrice: '',
  overridePriceStartAt: '',
  overridePriceEndAt: '',
  ignoreSalePrice: false,
  ignoreRebate: false,
  keepRebate: false,
};

export const formatDateInput = (value?: string | Date | null): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

export const normalizeDescriptionForComparison = (value?: string): string =>
  String(value || '').replace(/\s+/g, ' ').trim();

export const parseOverrideTagsInput = (value: string): string[] =>
  value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

export const buildOverridePayload = (
  form: OverrideFormState,
  product: CameraWithPricing
): Partial<StoreProductOverride> => {
  const overridePrice =
    form.overridePrice.trim().length > 0 ? Number(form.overridePrice) : undefined;
  const defaultDescription = normalizeDescriptionForComparison(
    stripHtml(product.description || '')
  );
  const candidateOverrideDescription = normalizeDescriptionForComparison(
    form.overrideDescription || ''
  );
  const shouldPersistOverrideDescription =
    candidateOverrideDescription.length > 0 && candidateOverrideDescription !== defaultDescription;

  const defaultTitle = (product.name || product.model || '').trim();
  const candidateTitle = form.overrideTitle.trim();
  const shouldPersistOverrideTitle =
    candidateTitle.length > 0 && candidateTitle !== defaultTitle;

  const tagsInput = form.overrideTags.trim();
  // Empty tags field clears store tag override (Motzu auto tags resume).
  const overrideTags = tagsInput.length > 0 ? parseOverrideTagsInput(tagsInput) : null;

  return {
    overrideTitle: shouldPersistOverrideTitle ? candidateTitle : null,
    overrideDescription: shouldPersistOverrideDescription ? form.overrideDescription.trim() : null,
    overrideTags,
    overridePrice: Number.isFinite(overridePrice) ? overridePrice : null,
    overridePriceStartAt: form.overridePriceStartAt || null,
    overridePriceEndAt: form.overridePriceEndAt || null,
    ignoreSalePrice: form.ignoreSalePrice,
    ignoreRebate: form.ignoreRebate,
    keepRebate: form.keepRebate,
    model: product.model || null,
    brand: product.brand || null,
    sku: product.sku || null,
  } as Partial<StoreProductOverride>;
};

export const buildAIDescriptionPayload = (product: CameraWithPricing) => ({
  name: product.name || product.model,
  brand: product.brand,
  model: product.model,
  sku: product.sku,
  category: product.category,
  productType: product.productType,
  description: stripHtml(product.description || ''),
  specifications: product.specifications || {},
});

// ---------------------------------------------------------------------------
// Price/currency formatters — a hook because currency formatting depends on
// LocaleContext (region-default currency when a product carries none).
// ---------------------------------------------------------------------------

export function useProductPriceFormatters() {
  const { formatCurrency, currency: regionCurrency } = useLocale();

  const getProductCurrency = (product: Product): 'USD' | 'CAD' => {
    const rawCurrency = String(product.currency || '').toUpperCase().trim();
    if (rawCurrency === 'USD' || rawCurrency === 'CAD') {
      return rawCurrency;
    }
    const formatted = (product.priceFormatted || '').trim();
    if (formatted.startsWith('C$') || formatted.includes('CAD')) {
      return 'CAD';
    }
    // Missing currency: fall back to the deployment region default
    // (CA → CAD, US → USD) — the label is still always shown.
    return regionCurrency === 'CAD' ? 'CAD' : 'USD';
  };

  const formatAmountForProduct = (product: Product, amount?: number): string => {
    if (!amount || amount <= 0) return '';
    const currency = getProductCurrency(product);
    return `${formatCurrency(amount, currency)} ${currency}`;
  };

  /**
   * Locale-correct currency symbol for this product's currency, without the
   * trailing "USD"/"CAD" that `formatAmountForProduct` appends — for figures
   * that sit alongside an already-labelled price (MAP tiles, price history).
   */
  const formatMoneyForProduct = (product: Product, amount?: number | null): string | null => {
    if (amount == null || !Number.isFinite(Number(amount))) return null;
    return formatCurrency(Number(amount), getProductCurrency(product));
  };

  const formatProductPrice = (product: Product): string => {
    if (product.price && product.price > 0) {
      return formatAmountForProduct(product, product.price);
    }
    const formatted = product.priceFormatted || '';
    if (!formatted || formatted === '$0.00') return '';
    const currency = getProductCurrency(product);
    if (!formatted.includes('USD') && !formatted.includes('CAD')) {
      return `${formatted} ${currency}`;
    }
    return formatted;
  };

  const saleDisplay = (product: CameraWithPricing) => {
    const sale = resolveDisplaySale(product, product.pricingData);
    return {
      ...sale,
      priceLabel: sale.sellingPrice > 0 ? formatAmountForProduct(product, sale.sellingPrice) : formatProductPrice(product),
      regularLabel:
        sale.isSaleActive && sale.regularPrice > 0
          ? formatAmountForProduct(product, sale.regularPrice)
          : '',
      savingsLabel: sale.savingsAmount > 0 ? formatAmountForProduct(product, sale.savingsAmount) : '',
    };
  };

  const rebateDisplay = (product: CameraWithPricing) =>
    resolveRebateDisplay(product.pricing, product.pricingData);

  const dealerReimbursement = (product: CameraWithPricing): number => {
    const amount = rebateDisplay(product).tradeCredit;
    return amount > 0 ? amount : 0;
  };

  const dealerCost = (product: CameraWithPricing): number => resolveDealerCost(product);
  const dealerCostDetail = (product: CameraWithPricing): DealerCostResolution | null => resolveDealerCostWithSource(product);

  const getPricingStatus = (product: CameraWithPricing): { label: string; color: 'success' | 'warning' | 'default' } => {
    const em = getEffectiveMapForProduct(product);
    if (em?.raw && em.raw > 0) {
      return { label: 'MAP Available', color: 'success' };
    }
    if ((em?.instantRebate && em.instantRebate > 0) || (product.pricingData?.instantRebate && product.pricingData.instantRebate > 0)) {
      return { label: 'Promo Active', color: 'warning' };
    }
    if (product.productType === 'camera') {
      return { label: 'MAP Missing', color: 'default' };
    }
    if (product.price && product.price > 0) {
      return { label: 'Retail Price', color: 'success' };
    }
    return { label: 'No Price Data', color: 'default' };
  };

  return {
    getProductCurrency,
    formatAmountForProduct,
    formatMoneyForProduct,
    formatProductPrice,
    saleDisplay,
    rebateDisplay,
    dealerReimbursement,
    dealerCost,
    dealerCostDetail,
    getPricingStatus,
  };
}
